import { Box, Skeleton } from '@cdl/heartbeat-react';
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useScale } from '../hooks/useScale';
import { Scale } from '../types/scale';
import { ClusterBeat } from '../../clustering/clustering.types';
import { drawBeats } from '../utils/canvas.util';

interface Context {
  width: number;
  height: number;
  id: number;
  backgroundRef: React.RefObject<HTMLElement>;
  foregroundRef: React.RefObject<HTMLElement>;
  xScale: Scale;
  yScale: Scale;
}
export const CanvasContext = React.createContext<Context | null>(null);
type Props = PropsWithChildren<{
  clusterId: number;
  beats: ClusterBeat[];
  width: number;
  height: number;
  xAxis: {
    xMax: number;
    xMin?: number;
  };
  yAxis: {
    yMax: number;
    yMin?: number;
  };
  shadowBeats?: ClusterBeat[];
  offsetY?: number;
  inverseBeats?: boolean;
}>;

export const CanvasStrip: React.FC<Props> = ({
  children,
  clusterId,
  beats,
  width,
  height,
  xAxis: { xMax, xMin = 0 },
  yAxis: { yMax, yMin = 0 },
  shadowBeats,
  offsetY = 0,
  inverseBeats = false,
}) => {
  const stripRef = useRef<HTMLCanvasElement>(null);
  const shadowRef = useRef<HTMLCanvasElement>(null);
  const rectangleRef = useRef<HTMLCanvasElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const foregroundRef = useRef<HTMLDivElement>(null);
  const [refHasRendered, setRefHasRendered] = useState(false);

  const xScale = useScale({
    domain: { start: xMin, end: xMax },
    range: { end: width },
  });
  const yScale = useScale({
    domain: { start: yMax, end: yMin },
    range: { end: height },
  });

  useEffect(() => {
    if (stripRef.current != null) {
      drawBeats(
        xScale,
        yScale,
        width,
        height + 2 * offsetY,
        stripRef.current,
        beats,
        rectangleRef.current,
        false,
        offsetY,
      );
      setRefHasRendered(true);
    }
  }, [xScale, yScale, beats, width, height, stripRef, rectangleRef, offsetY]);

  useEffect(() => {
    if (shadowRef.current != null) {
      drawBeats(
        xScale,
        yScale,
        width,
        height + 2 * offsetY,
        shadowRef.current,
        shadowBeats,
        undefined,
        true,
        offsetY,
      );
    }
  }, [shadowBeats, xScale, yScale, shadowRef, width, height, offsetY]);

  return (
    <Box width={width} height={height + 2 * offsetY}>
      <Box ref={backgroundRef} />
      <CanvasContext.Provider
        value={{
          width,
          height: height + 2 * offsetY,
          id: clusterId,
          backgroundRef,
          foregroundRef,
          xScale,
          yScale,
        }}
      >
        {refHasRendered && children}
      </CanvasContext.Provider>
      <canvas
        ref={rectangleRef}
        style={{ position: 'absolute' }}
        height={height}
      />
      <canvas
        ref={inverseBeats ? stripRef : shadowRef}
        style={{ position: 'absolute' }}
        height={height}
      />
      <canvas
        ref={inverseBeats ? shadowRef : stripRef}
        style={{ position: 'absolute' }}
        height={height}
      />
      <Box ref={foregroundRef} />
      {!refHasRendered && (
        <Skeleton
          variant='rectangular'
          width={width}
          height={height + 2 * offsetY}
        />
      )}
    </Box>
  );
};

export const useCanvasStrip = () => {
  const context = useContext(CanvasContext);
  if (context == null) {
    throw new Error('useCanvas must be used inside <CanvasStrip />');
  }

  return context;
};
